import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

import { usersService } from "../../services/usersService";
import { UserListResponse, User, Role } from "../../types/users";
import PageTitle from "../../components/common/PageTitle";
import UsersTable from "../../components/users/UsersTable";
import UserFormModal from "../../components/users/UserFormModal";

const UsersPage: React.FC = () => {
  const [userListData, setUserListData] = useState<UserListResponse | null>(
    null,
  );
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch users and roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch users and roles in parallel
        const [userListResponse, rolesResponse] = await Promise.all([
          usersService.getUsers({
            skip: (page - 1) * pageSize,
            limit: pageSize,
            search: searchQuery,
          }),
          usersService.getRoles(),
        ]);

        setUserListData(userListResponse);
        setRoles(rolesResponse);
      } catch (err) {
        console.error("Failed to fetch users data:", err);
        setError("Failed to load users. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, pageSize, searchQuery, refreshTrigger]);

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
  };

  // Open modal for creating a new user
  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserModal(true);
  };

  // Open modal for editing a user
  //@ts-ignore
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  // Handle user save (create or update)
  const handleSaveUser = async (userData: any, password?: string) => {
    try {
      setLoading(true);

      if (selectedUser) {
        // Update existing user
        await usersService.updateUser(selectedUser.id, userData);

        // Update password if provided
        if (password) {
          // In a real app, you would encrypt the password here
          // const encryptedPassword = await authService.encryptPassword(password);
          await usersService.updateUserPassword(selectedUser.id, password);
        }
      } else {
        // Create new user
        if (!password) {
          throw new Error("Password is required for new users");
        }

        // In a real app, you would encrypt the password here
        // const encryptedPassword = await authService.encryptPassword(password);
        // await usersService.createUserEncrypted({
        //   ...userData,
        //   encrypted_password: encryptedPassword
        // });

        await usersService.createUser({
          ...userData,
          password,
        });
      }

      // Close modal and refresh data
      setShowUserModal(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to save user:", err);
      setError("Failed to save user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle user deletion (deactivation)
  const handleDeleteUser = async (userId: number) => {
    try {
      setLoading(true);
      await usersService.updateUserStatus(userId, false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to deactivate user:", err);
      setError("Failed to deactivate user. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle user status update
  // const handleUpdateUserStatus = async (
  //   userId: number,
  //   email: string,
  //   isActive: boolean,
  // ) => {
  //   try {
  //     setLoading(true);
  //     await usersService.updateUser(userId, {
  //       email,
  //       is_active: isActive,
  //     });
  //     setRefreshTrigger((prev) => prev + 1);
  //   } catch (err) {
  //     console.error("Failed to update user status:", err);
  //     setError("Failed to update user status. Please try again.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  return (
    <div className="space-y-6">
      <PageTitle
        actions={
          <Button color="primary" onPress={handleCreateUser}>
            Add User
          </Button>
        }
        subtitle="Manage your system users and permissions"
        title="Users"
      />

      {/* Search and filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4">
        <form className="flex gap-2" onSubmit={handleSearch}>
          <Input
            className="flex-1"
            placeholder="Search by name, username, or email"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Button color="primary" type="submit">
            Search
          </Button>
        </form>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Users table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <UsersTable
          loading={loading}
          pagination={{
            page,
            pageSize,
            total: userListData?.total || 0,
          }}
          users={userListData?.users || []}
          onUserDeleted={handleDeleteUser}
          // onUserUpdated={handleEditUser}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onUserCreated={handleCreateUser}
          roles={roles}
          // onUpdate={handleUpdateUserStatus}
        />
      </div>

      {/* User form modal */}
      {showUserModal && (
        <UserFormModal
          loading={loading}
          roles={roles}
          user={selectedUser}
          onCancel={() => setShowUserModal(false)}
          onSave={handleSaveUser}
        />
      )}
    </div>
  );
};

export default UsersPage;
